<template>
  <div class="font-sans bg-gray-50 text-gray-800">
    <!-- Header -->
    <header id="header" class="bg-white border-b border-gray-200 fixed w-full z-10">
      <div class="flex items-center px-4 py-3">
        <div class="flex items-center">
          <router-link to="/" class="text-xl font-bold flex items-center text-custom-button-blue hover:text-custom-dark-blue">
            <img
              src="@/assets/images/logos/sidebarlogo.png"
              alt="ClickAI"
              class="h-10"
            />
          </router-link>
        </div>
      </div>
    </header>

    <div id="content-wrapper" class="flex pt-14">
      <!-- Sidebar Navigation -->
      <div id="sidebar" class="fixed top-14 left-0 w-64 h-screen bg-white overflow-y-auto border-r border-gray-200">
        <div class="px-4 py-4">
          <ul class="space-y-1">
            <li>
              <router-link to="/documentation" class="block py-2 px-3 text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md font-medium cursor-pointer">
                <i class="fa-solid fa-book-open mr-2"></i> Introduction
              </router-link>
            </li>
            
            <li class="py-1">
              <div 
                @click="toggleSection('Leads')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Manage potential customers and track their interactions"
              >
                <span><i class="fa-solid fa-user-plus mr-2"></i> Leads</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Leads') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Leads')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/leads" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/leads-custom-fields" class="block py-1 text-sm cursor-pointer"
                    :class="$route.path === '/endpoint/leads-custom-fields' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-green-100 text-green-800 px-1 rounded mr-2">GET</span>
                    Get custom fields
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/leads-new-lead" class="block py-1 text-sm cursor-pointer"
                    :class="$route.path === '/endpoint/leads-new-lead' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Create new lead
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/leads-edit-lead" class="block py-1 text-sm cursor-pointer"
                    :class="$route.path === '/endpoint/leads-edit-lead' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Edit lead
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/leads-get-lead" class="block py-1 text-sm cursor-pointer"
                    :class="$route.path === '/endpoint/leads-get-lead' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get lead by ID
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/leads-get-lead-phone" class="block py-1 text-sm cursor-pointer"
                    :class="$route.path === '/endpoint/leads-get-lead-phone' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get leads by phone
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/leads-add-call-log" class="block py-1 text-sm cursor-pointer"
                    :class="$route.path === '/endpoint/leads-add-call-log' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Add call log
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="py-1">
              <div 
                @click="toggleSection('Deals')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Track sales opportunities and manage deal pipeline"
              >
                <span><i class="fa-solid fa-handshake mr-2"></i> Deals</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Deals') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Deals')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/deals" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/deals-custom-fields" class="block py-1 text-sm cursor-pointer"
                    :class="$route.path === '/endpoint/deals-custom-fields' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-green-100 text-green-800 px-1 rounded mr-2">GET</span>
                    Get custom fields
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/deals-new-deal" class="block py-1 text-sm cursor-pointer"
                    :class="$route.path === '/endpoint/deals-new-deal' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Create new deal
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/deals-edit-deal" class="block py-1 text-sm cursor-pointer"
                    :class="$route.path === '/endpoint/deals-edit-deal' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Edit deal
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/deals-get-deal" class="block py-1 text-sm cursor-pointer"
                    :class="$route.path === '/endpoint/deals-get-deal' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get deal by ID
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/deals-get-deal-phone" class="block py-1 text-sm cursor-pointer"
                    :class="$route.path === '/endpoint/deals-get-deal-phone' ? 'text-custom-button-blue hover:text-custom-dark-blue font-medium bg-custom-blue bg-opacity-10 rounded px-2' : 'text-gray-600 hover:text-gray-800'">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get deals by phone
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="py-1">
              <div 
                @click="toggleSection('Customers')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Manage customer profiles and relationships"
              >
                <span><i class="fa-solid fa-users mr-2"></i> Customers</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Customers') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Customers')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/customers" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/customers-custom-fields" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-green-100 text-green-800 px-1 rounded mr-2">GET</span>
                    Get custom fields
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/customers-create-customer" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Create customer
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/customers-get-customer" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get customer by ID
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/customers-get-customer-phone" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get customers by phone
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/customers-edit-customer" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Edit customer
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/customers-add-call-log" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Add call log
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="py-1">
              <div 
                @click="toggleSection('Products')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Manage product catalog and variations"
              >
                <span><i class="fa-solid fa-box mr-2"></i> Products</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Products') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Products')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/products" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/products-get-all" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-green-100 text-green-800 px-1 rounded mr-2">GET</span>
                    Get all products
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/products-get-product" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get product by ID
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/products-create-product" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Create product
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/products-edit-product" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Edit product
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/products-add-variations" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Add variations
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/products-delete-variation" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-red-100 text-red-800 px-1 rounded mr-2">DEL</span>
                    Delete variation
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="py-1">
              <div 
                @click="toggleSection('Calendar')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Schedule meetings and manage appointments"
              >
                <span><i class="fa-solid fa-calendar mr-2"></i> Calendar</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Calendar') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Calendar')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/calendar" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-get-appointment-types" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-green-100 text-green-800 px-1 rounded mr-2">GET</span>
                    Get appointment types
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-get-appointment-users" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get appointment users
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-appointment-type-details" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get appointment details
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-get-user-working-hours" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get user working hours
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-get-break-hours" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get break hours
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-get-free-time-slot" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get free time slots
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-get-double-free-time-slot" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get double free slots
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-get-user-appointment-by-date" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get user appointments
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-schedule-meeting" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Schedule meeting
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-create-event-type" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Create event type
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-edit-event-type" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Edit event type
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="py-1">
              <div 
                @click="toggleSection('Team')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Manage team members and user accounts"
              >
                <span><i class="fa-solid fa-users-gear mr-2"></i> Team</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Team') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Team')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/team" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/team-list" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-green-100 text-green-800 px-1 rounded mr-2">GET</span>
                    Get team members
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="py-1">
              <div 
                @click="toggleSection('Tasks')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Create and manage tasks and assignments"
              >
                <span><i class="fa-solid fa-list-check mr-2"></i> Tasks</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Tasks') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Tasks')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/tasks" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/tasks-create-task" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Create task
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/tasks-edit-task" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Edit task
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/tasks-delete-task" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-red-100 text-red-800 px-1 rounded mr-2">DEL</span>
                    Delete task
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="py-1">
              <div 
                @click="toggleSection('Contacts')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Manage contact information and address book"
              >
                <span><i class="fa-solid fa-address-book mr-2"></i> Contacts</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Contacts') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Contacts')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/contacts" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/contacts-create-contact" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Create new contact
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/contacts-find-by-phone" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Find by phone
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/contacts-find-by-name" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Find by name
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="py-1">
              <div 
                @click="toggleSection('Chats')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Send messages and manage chat communications"
              >
                <span><i class="fa-solid fa-comments mr-2"></i> Chats</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Chats') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Chats')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/chats" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/chats-send-message" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Send message
                  </router-link>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </div>

      <!-- Main Content -->
      <div id="main-content" class="ml-64 flex-1 px-8 py-6">
        <div class="w-full">
          <!-- Header -->
          <div id="endpoint-header" class="mb-8">
            <div class="flex items-center mb-3">
              <div :class="'bg-custom-blue p-2 rounded-md text-custom-button-blue mr-3'">
                <i :class="getCurrentCategory().icon"></i>
              </div>
              <h1 class="text-3xl font-bold text-gray-800">
                {{ getCurrentCategory().title }} API
              </h1>
            </div>
            <p class="text-gray-600 text-lg mb-6">
              {{ getCurrentCategory().description }}
            </p>
            
            <div class="flex items-center space-x-2 mb-6">
              <div class="relative">
                <select 
                  v-model="selectedLanguage" 
                  class="pl-3 pr-3 py-1 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-1 focus:ring-custom-button-blue"
                >
                  <option value="javascript">JavaScript</option>
                  <option value="python">Python</option>
                  <option value="curl">cURL</option>
                  <option value="php">PHP</option>
                  <option value="ruby">Ruby</option>
                </select>
              </div>
              <div class="bg-gray-100 text-gray-700 px-3 py-1 rounded-md text-sm font-medium">
                <i class="fa-solid fa-code mr-1"></i> {{ getCurrentCategory().endpoints.length }} Endpoints
              </div>
            </div>
          </div>

          <!-- Endpoints List -->
          <div id="endpoints-list" class="mb-10">
            <div class="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
              <router-link 
                v-for="(endpoint, index) in getCurrentCategory().endpoints" 
                :key="endpoint.name"
                :to="getEndpointLink(endpoint)"
                :class="index < getCurrentCategory().endpoints.length - 1 ? 'border-b border-gray-200' : ''"
                class="block"
              >
                <div class="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition">
                  <div class="flex items-center">
                    <span :class="getMethodClass(endpoint.method)" class="text-xs font-medium rounded-md mr-3 px-2 py-1">
                      {{ endpoint.method }}
                    </span>
                    <h3 class="font-medium text-gray-800">{{ endpoint.name }}</h3>
                  </div>
                  <div class="flex items-center">
                    <span class="text-gray-500 text-sm mr-3">{{ endpoint.path }}</span>
                    <i class="fa-solid fa-chevron-right text-gray-400"></i>
                  </div>
                </div>
              </router-link>
            </div>
          </div>

          <!-- Example Section -->
          <div id="example-section" class="mb-10">
            <h2 class="text-2xl font-bold text-gray-800 mb-4">
              Example: {{ getFirstEndpoint().name }}
            </h2>
            <div class="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-6">
              <div class="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 class="font-semibold">Request Example</h3>
                <div class="flex items-center space-x-2">
                  <button class="px-3 py-1 bg-custom-button-blue text-white rounded text-sm font-medium hover:bg-custom-button-blue transition">
                    <i class="fa-solid fa-play mr-1"></i> Try it
                  </button>
                </div>
              </div>
              <div class="bg-gray-800 text-gray-200 p-4 font-mono text-sm overflow-x-auto">
                <pre v-if="selectedLanguage === 'javascript'" class="language-javascript">{{ getCodeExample('javascript') }}</pre>
                <pre v-if="selectedLanguage === 'python'" class="language-python">{{ getCodeExample('python') }}</pre>
                <pre v-if="selectedLanguage === 'curl'" class="language-bash">{{ getCodeExample('curl') }}</pre>
                <pre v-if="selectedLanguage === 'php'" class="language-php">{{ getCodeExample('php') }}</pre>
                <pre v-if="selectedLanguage === 'ruby'" class="language-ruby">{{ getCodeExample('ruby') }}</pre>
              </div>
              <div class="p-4 bg-gray-50 border-t border-gray-200 flex justify-end">
                <button 
                  @click="copyCodeToClipboard"
                  class="px-3 py-1 bg-custom-button-blue text-white rounded text-sm font-medium hover:bg-custom-button-blue transition"
                  :class="{ 'bg-green-600 hover:bg-green-700': copied }"
                >
                  <i :class="copied ? 'fa-solid fa-check' : 'fa-solid fa-copy'" class="mr-1"></i> 
                  {{ copied ? 'Copied!' : 'Copy Code' }}
                </button>
              </div>
            </div>
          </div>

          <!-- Response Section -->
          <div id="response-section" class="mb-10">
            <h2 class="text-2xl font-bold text-gray-800 mb-4">
              Response Format
            </h2>
            <div class="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-6">
              <div class="p-4 border-b border-gray-200">
                <h3 class="font-semibold">Example Response</h3>
              </div>
              <div class="bg-gray-800 text-gray-200 p-4 font-mono text-sm overflow-x-auto">
                <pre>{{ getResponseExample() }}</pre>
              </div>
            </div>
          </div>

          <!-- Related Endpoints -->
          <div id="related-endpoints" class="mb-10">
            <h2 class="text-2xl font-bold text-gray-800 mb-4">
              Related Endpoints
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <router-link 
                v-for="endpoint in getRelatedEndpoints()" 
                :key="endpoint.name"
                :to="getEndpointLink(endpoint)"
                class="bg-white p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition cursor-pointer block"
              >
                <div class="flex items-center">
                  <span :class="getMethodClass(endpoint.method)" class="text-xs font-medium rounded-md mr-3 px-2 py-1">
                    {{ endpoint.method }}
                  </span>
                  <h3 class="font-medium text-gray-800">{{ endpoint.name }}</h3>
                </div>
                <p class="text-gray-600 text-sm mt-2">
                  {{ endpoint.description }}
                </p>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()
const currentCategory = ref('')
const currentEndpoint = ref('')
const openSections = ref<string[]>([])
const selectedLanguage = ref('javascript')
const copied = ref(false)

// Categories and their endpoints data
const categoriesData = {
  leads: {
    title: 'Leads',
    icon: 'fa-solid fa-user-plus',
    description: 'Manage potential customers and track their interactions through your leads API endpoints.',
    endpoints: [
      { name: 'Get custom fields', method: 'GET', path: '/leads/custom-fields', description: 'Retrieve all custom fields for leads' },
      { name: 'Create new lead', method: 'POST', path: '/leads/new-lead', description: 'Add a new lead to your system' },
      { name: 'Edit lead', method: 'POST', path: '/leads/edit-lead', description: 'Update existing lead information' },
      { name: 'Get lead by ID', method: 'POST', path: '/leads/get-lead', description: 'Retrieve specific lead details' },
      { name: 'Get leads by phone', method: 'POST', path: '/leads/get-lead/phone', description: 'Find leads by phone number' },
      { name: 'Add call log', method: 'POST', path: '/leads/add-call-log', description: 'Log calls and interactions' }
    ]
  },
  deals: {
    title: 'Deals',
    icon: 'fa-solid fa-handshake',
    description: 'Track sales opportunities and manage deal pipeline through your deals API endpoints.',
    endpoints: [
      { name: 'Get custom fields', method: 'GET', path: '/deals/custom-fields', description: 'Retrieve all custom fields for deals' },
      { name: 'Create new deal', method: 'POST', path: '/deals/new-deal', description: 'Create a new deal opportunity' },
      { name: 'Edit deal', method: 'POST', path: '/deals/edit-deal', description: 'Update existing deal information' },
      { name: 'Get deal by ID', method: 'POST', path: '/deals/get-deal', description: 'Retrieve specific deal details' },
      { name: 'Get deals by phone', method: 'POST', path: '/deals/get-deal/phone', description: 'Find deals by phone number' }
    ]
  },
  customers: {
    title: 'Customers',
    icon: 'fa-solid fa-users',
    description: 'Manage customer profiles and relationships through your customers API endpoints.',
    endpoints: [
      { name: 'Get custom fields', method: 'GET', path: '/customers/custom-fields', description: 'Retrieve all custom fields for customers' },
      { name: 'Create customer', method: 'POST', path: '/customers/create-customer', description: 'Add a new customer profile' },
      { name: 'Get customer by ID', method: 'POST', path: '/customers/get-customer', description: 'Retrieve specific customer details' },
      { name: 'Get customers by phone', method: 'POST', path: '/customers/get-customer/phone', description: 'Find customers by phone number' },
      { name: 'Edit customer', method: 'POST', path: '/customers/edit-customer', description: 'Update customer information' },
      { name: 'Add call log', method: 'POST', path: '/customers/add-call-log', description: 'Log customer interactions' }
    ]
  },
  products: {
    title: 'Products',
    icon: 'fa-solid fa-box',
    description: 'Manage your product catalog, inventory, and pricing through your products API endpoints.',
    endpoints: [
      { name: 'Get all products', method: 'GET', path: '/products/all', description: 'Retrieve all products in organization' },
      { name: 'Get single product', method: 'POST', path: '/product/get-product', description: 'Get product with variations' },
      { name: 'Edit product', method: 'POST', path: '/products/edit-product', description: 'Update product with variations' },
      { name: 'Add variations', method: 'POST', path: '/products/add-variations', description: 'Add new product variations' },
      { name: 'Delete variation', method: 'POST', path: '/products/delete-variation', description: 'Remove product variation' },
      { name: 'Create product', method: 'POST', path: '/products/create-product', description: 'Create new product with variations' }
    ]
  },
  calendar: {
    title: 'Calendar & Appointments',
    icon: 'fa-solid fa-calendar',
    description: 'Schedule meetings, manage availability, and automate appointment reminders through your calendar API endpoints.',
    endpoints: [
      { name: 'Get appointment types', method: 'GET', path: '/calendar/get-appointment-types', description: 'Retrieve all appointment types' },
      { name: 'Get appointment users', method: 'POST', path: '/calendar/get-appointment-users', description: 'Get users with appointment access' },
      { name: 'Get appointment details', method: 'POST', path: '/calendar/appointment-type-details', description: 'Get appointment type details' },
      { name: 'Get working hours', method: 'POST', path: '/calendar/get-user-working-hours', description: 'Retrieve user working hours' },
      { name: 'Get break hours', method: 'POST', path: '/calendar/get-break-hours-by-date', description: 'Get user break hours' },
      { name: 'Get free time slots', method: 'POST', path: '/calendar/get-free-time-slot', description: 'Find available time slots' },
      { name: 'Get user appointments', method: 'POST', path: '/appointment/get-user-appointment-by-date', description: 'Get appointments by date' },
      { name: 'Schedule meeting', method: 'POST', path: '/calendar/schedule-meeting', description: 'Create new appointment' },
      { name: 'Create event type', method: 'POST', path: '/calendar/create-event-type', description: 'Create appointment type' },
      { name: 'Edit event type', method: 'POST', path: '/calendar/edit-event-type', description: 'Update appointment type' },
      { name: 'Get double free slots', method: 'POST', path: '/calendar/get-double-free-time-slot', description: 'Find overlapping free slots' }
    ]
  },
  team: {
    title: 'Team',
    icon: 'fa-solid fa-users-gear',
    description: 'Manage team members and user accounts through your team API endpoints.',
    endpoints: [
      { name: 'Get team members', method: 'GET', path: '/team/list', description: 'Retrieve list of team members' }
    ]
  },
  tasks: {
    title: 'Tasks',
    icon: 'fa-solid fa-list-check',
    description: 'Create and manage tasks and assignments through your tasks API endpoints.',
    endpoints: [
      { name: 'Create task', method: 'POST', path: '/tasks/create-task', description: 'Create a new task' },
      { name: 'Edit task', method: 'POST', path: '/tasks/edit-task', description: 'Update existing task' },
      { name: 'Delete task', method: 'POST', path: '/tasks/delete-task', description: 'Remove task from system' }
    ]
  },
  contacts: {
    title: 'Contacts',
    icon: 'fa-solid fa-address-book',
    description: 'Manage contact information and address book through your contacts API endpoints.',
    endpoints: [
      { name: 'Create new contact', method: 'POST', path: '/contacts/create-new-contact', description: 'Add new contact to system' },
      { name: 'Find by phone', method: 'POST', path: '/contacts/find-by-phone', description: 'Search contacts by phone number' },
      { name: 'Find by name', method: 'POST', path: '/contacts/find-by-name', description: 'Search contacts by name' }
    ]
  },
  chats: {
    title: 'Chats',
    icon: 'fa-solid fa-comments',
    description: 'Send messages and manage chat communications through your chats API endpoints.',
    endpoints: [
      { name: 'Send message', method: 'POST', path: '/chats/send-message', description: 'Send message to contact' }
    ]
  }
}

const categories = computed(() => Object.entries(categoriesData).map(([key, data]) => ({
  key,
  ...data
})))

const getCurrentCategory = () => {
  return categoriesData[currentCategory.value] || categoriesData.leads
}

const getFirstEndpoint = () => {
  return getCurrentCategory().endpoints[0] || { name: 'Unknown', method: 'GET' }
}

const getMethodClass = (method: string) => {
  switch (method) {
    case 'GET': return 'bg-green-100 text-green-800'
    case 'POST': return 'bg-blue-100 text-blue-800'
    case 'PUT': return 'bg-yellow-100 text-yellow-800'
    case 'DELETE': return 'bg-red-100 text-red-800'
    default: return 'bg-gray-100 text-gray-800'
  }
}

const getRelatedEndpoints = () => {
  const current = getCurrentCategory()
  return current.endpoints.slice(1, 3) // Get 2 related endpoints
}

const getEndpointLink = (endpoint: any) => {
  const category = currentCategory.value
  
  // מיפוי של שמות endpoints לIDs שלהם
  const endpointMap: { [key: string]: { [endpointName: string]: string } } = {
    leads: {
      'Get custom fields': 'leads-custom-fields',
      'Create new lead': 'leads-new-lead', 
      'Edit lead': 'leads-edit-lead',
      'Get lead by ID': 'leads-get-lead',
      'Get leads by phone': 'leads-get-lead-phone',
      'Add call log': 'leads-add-call-log'
    },
    deals: {
      'Get custom fields': 'deals-custom-fields',
      'Create new deal': 'deals-new-deal',
      'Edit deal': 'deals-edit-deal', 
      'Get deal by ID': 'deals-get-deal',
      'Get deals by phone': 'deals-get-deal-phone'
    },
    customers: {
      'Get custom fields': 'customers-custom-fields',
      'Create customer': 'customers-create-customer',
      'Get customer by ID': 'customers-get-customer',
      'Get customers by phone': 'customers-get-customer-phone',
      'Edit customer': 'customers-edit-customer',
      'Add call log': 'customers-add-call-log'
    },
    products: {
      'Get all products': 'products-get-all',
      'Get single product': 'products-get-product',
      'Create product': 'products-create-product',
      'Edit product': 'products-edit-product',
      'Add variations': 'products-add-variations',
      'Delete variation': 'products-delete-variation'
    },
    calendar: {
      'Get appointment types': 'calendar-get-appointment-types',
      'Get appointment users': 'calendar-get-appointment-users', 
      'Get appointment details': 'calendar-appointment-type-details',
      'Get working hours': 'calendar-get-user-working-hours',
      'Get break hours': 'calendar-get-break-hours',
      'Get free time slots': 'calendar-get-free-time-slot',
      'Get user appointments': 'calendar-get-user-appointment-by-date',
      'Schedule meeting': 'calendar-schedule-meeting',
      'Create event type': 'calendar-create-event-type',
      'Edit event type': 'calendar-edit-event-type',
      'Get double free slots': 'calendar-get-double-free-time-slot'
    },
    team: {
      'Get team members': 'team-list'
    },
    tasks: {
      'Create task': 'tasks-create-task',
      'Edit task': 'tasks-edit-task',
      'Delete task': 'tasks-delete-task'
    },
    contacts: {
      'Create new contact': 'contacts-create-contact',
      'Find by phone': 'contacts-find-by-phone',
      'Find by name': 'contacts-find-by-name'
    },
    chats: {
      'Send message': 'chats-send-message'
    }
  }
  
  const categoryEndpoints = endpointMap[category] || {}
  const endpointId = categoryEndpoints[endpoint.name]
  
  if (endpointId) {
    return `/endpoint/${endpointId}`
  } else {
    // fallback
    console.warn(`No endpoint mapping found for ${endpoint.name} in category ${category}`)
    return '/documentation'
  }
}

const toggleSection = (section: string) => {
  const index = openSections.value.indexOf(section)
  if (index > -1) {
    openSections.value = []
  } else {
    openSections.value = [section]
  }
}

const getCodeExample = (language: string) => {
  const category = currentCategory.value
  const endpoint = getFirstEndpoint()
  
  // דוגמאות קוד אמיתיות מהדוקומנטציה לפי endpoint ושפה
  const realCodeExamples: { [key: string]: { [lang: string]: string } } = {
    'leads/custom-fields': {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/leads/custom-fields';

fetch(url, {
    method: 'GET',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    }
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        console.log('Custom fields:', data.data);
    } else {
        console.error('Error:', data.error);
    }
})
.catch(error => console.error('Request failed:', error));`,
      
      python: `import requests

api_key = 'your_api_key_here'
url = 'https://dev.clickai.live/leads/custom-fields'

headers = {
    'Authorization': f'Bearer {api_key}',
    'Content-Type': 'application/json'
}

response = requests.get(url, headers=headers)

if response.status_code == 200:
    data = response.json()
    if data['success']:
        print('Custom fields:', data['data'])
    else:
        print('Error:', data['error'])
else:
    print(f'HTTP Error {response.status_code}: {response.text}')`,
      
      curl: `curl -X GET "https://dev.clickai.live/leads/custom-fields" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json"`,
      
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/leads/custom-fields';

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 200) {
    $data = json_decode($response, true);
    echo "Custom fields: " . json_encode($data['data']);
} else {
    echo "Error $http_code: $response";
}
?>`,
      
      ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/leads/custom-fields')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Get.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'

response = http.request(request)

if response.code == '200'
  data = JSON.parse(response.body)
  puts "Custom fields: #{data['data']}"
else
  puts "Error #{response.code}: #{response.body}"
end`
    },
    
    'leads/new-lead': {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/leads/new-lead';

const leadData = {
    name: 'John Doe',
    phone: '050-1234567',
    status: 'new',
    source: 'website',
    hasCustomFields: false,
    email: 'yossi@example.com',
    company: 'חברת יוסי בע"מ'
};

fetch(url, {
    method: 'POST',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    },
    body: JSON.stringify(leadData)
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        console.log('Lead created successfully:', data.lead_id);
    } else {
        console.error('Error:', data.error);
    }
})
.catch(error => console.error('Request failed:', error));`,
      
      python: `import requests
import json

api_key = 'your_api_key_here'
url = 'https://dev.clickai.live/leads/new-lead'

headers = {
    'Authorization': f'Bearer {api_key}',
    'Content-Type': 'application/json'
}

lead_data = {
    'name': 'John Doe',
    'phone': '050-1234567',
    'status': 'new',
    'source': 'website',
    'hasCustomFields': False,
    'email': 'yossi@example.com',
    'company': 'חברת יוסי בע"מ'
}

response = requests.post(url, headers=headers, json=lead_data)

if response.status_code == 201:
    data = response.json()
    print(f'Lead created successfully: {data["lead_id"]}')
else:
    print(f'Error {response.status_code}: {response.text}')`,
      
      curl: `curl -X POST "https://dev.clickai.live/leads/new-lead" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "John Doe",
    "phone": "050-1234567",
    "status": "new",
    "source": "website",
    "hasCustomFields": false,
    "email": "yossi@example.com",
    "company": "חברת יוסי בע\\"מ"
  }'`,
      
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/leads/new-lead';

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$lead_data = [
    'name' => 'John Doe',
    'phone' => '050-1234567',
    'status' => 'new',
    'source' => 'website',
    'hasCustomFields' => false,
    'email' => 'yossi@example.com',
    'company' => 'חברת יוסי בע"מ'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($lead_data));
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 201) {
    $data = json_decode($response, true);
    echo "Lead created successfully: " . $data['lead_id'];
} else {
    echo "Error $http_code: $response";
}
?>`,
      
      ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/leads/new-lead')

lead_data = {
  'name' => 'John Doe',
  'phone' => '050-1234567',
  'status' => 'new',
  'source' => 'website',
  'hasCustomFields' => false,
  'email' => 'yossi@example.com',
  'company' => 'חברת יוסי בע"מ'
}

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'
request.body = lead_data.to_json

response = http.request(request)

if response.code == '201'
  data = JSON.parse(response.body)
  puts "Lead created successfully: #{data['lead_id']}"
else
  puts "Error #{response.code}: #{response.body}"
end`
    },
    
    'deals/new-deal': {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/deals/new-deal';

const dealData = {
    title: 'עסקה חדשה עם לקוח חשוב',
    description: 'עסקה לרבעון הרביעי',
    amount: 75000,
    name: 'דוד לוי',
    phone: '052-9876543',
    customer_id: '091cd08c-5dc3-456d-9d43-8918a549cfea',
    hasCustomFields: true,
    customFields: {
        'asd_1749674482286': 'תיאור נוסף של העסקה'
    }
};

fetch(url, {
    method: 'POST',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    },
    body: JSON.stringify(dealData)
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        console.log('Deal created successfully:', data.deal_id);
    } else {
        console.error('Error:', data.error);
    }
})
.catch(error => console.error('Request failed:', error));`,
      
      python: `import requests
import json

api_key = 'your_api_key_here'
url = 'https://dev.clickai.live/deals/new-deal'

headers = {
    'Authorization': f'Bearer {api_key}',
    'Content-Type': 'application/json'
}

deal_data = {
    'title': 'עסקה חדשה עם לקוח חשוב',
    'description': 'עסקה לרבעון הרביעי',
    'amount': 75000,
    'name': 'דוד לוי',
    'phone': '052-9876543',
    'customer_id': '091cd08c-5dc3-456d-9d43-8918a549cfea',
    'hasCustomFields': True,
    'customFields': {
        'asd_1749674482286': 'תיאור נוסף של העסקה'
    }
}

response = requests.post(url, headers=headers, json=deal_data)

if response.status_code == 201:
    data = response.json()
    print(f'Deal created successfully: {data["deal_id"]}')
else:
    print(f'Error {response.status_code}: {response.text}')`,
      
      curl: `curl -X POST "https://dev.clickai.live/deals/new-deal" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "עסקה חדשה עם לקוח חשוב",
    "description": "עסקה לרבעון הרביעי",
    "amount": 75000,
    "name": "דוד לוי",
    "phone": "052-9876543",
    "customer_id": "091cd08c-5dc3-456d-9d43-8918a549cfea",
    "hasCustomFields": true,
    "customFields": {
      "asd_1749674482286": "תיאור נוסף של העסקה"
    }
  }'`,
      
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/deals/new-deal';

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$deal_data = [
    'title' => 'עסקה חדשה עם לקוח חשוב',
    'description' => 'עסקה לרבעון הרביעי',
    'amount' => 75000,
    'name' => 'דוד לוי',
    'phone' => '052-9876543',
    'customer_id' => '091cd08c-5dc3-456d-9d43-8918a549cfea',
    'hasCustomFields' => true,
    'customFields' => [
        'asd_1749674482286' => 'תיאור נוסף של העסקה'
    ]
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($deal_data));
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 201) {
    $data = json_decode($response, true);
    echo "Deal created successfully: " . $data['deal_id'];
} else {
    echo "Error $http_code: $response";
}
?>`,
      
      ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/deals/new-deal')

deal_data = {
  'title' => 'עסקה חדשה עם לקוח חשוב',
  'description' => 'עסקה לרבעון הרביעי',
  'amount' => 75000,
  'name' => 'דוד לוי',
  'phone' => '052-9876543',
  'customer_id' => '091cd08c-5dc3-456d-9d43-8918a549cfea',
  'hasCustomFields' => true,
  'customFields' => {
    'asd_1749674482286' => 'תיאור נוסף של העסקה'
  }
}

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'
request.body = deal_data.to_json

response = http.request(request)

if response.code == '201'
  data = JSON.parse(response.body)
  puts "Deal created successfully: #{data['deal_id']}"
else
  puts "Error #{response.code}: #{response.body}"
end`
    },
    
    'deals/custom-fields': {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/deals/custom-fields';

fetch(url, {
    method: 'GET',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    }
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        console.log('Deal custom fields:', data.data);
    } else {
        console.error('Error:', data.error);
    }
})
.catch(error => console.error('Request failed:', error));`,
      
      python: `import requests

api_key = 'your_api_key_here'
url = 'https://dev.clickai.live/deals/custom-fields'

headers = {
    'Authorization': f'Bearer {api_key}',
    'Content-Type': 'application/json'
}

response = requests.get(url, headers=headers)

if response.status_code == 200:
    data = response.json()
    if data['success']:
        print('Deal custom fields:', data['data'])
    else:
        print('Error:', data['error'])
else:
    print(f'HTTP Error {response.status_code}: {response.text}')`,
      
      curl: `curl -X GET "https://dev.clickai.live/deals/custom-fields" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json"`,
      
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/deals/custom-fields';

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 200) {
    $data = json_decode($response, true);
    echo "Deal custom fields: " . json_encode($data['data']);
} else {
    echo "Error $http_code: $response";
}
?>`,
      
      ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/deals/custom-fields')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Get.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'

response = http.request(request)

if response.code == '200'
  data = JSON.parse(response.body)
  puts "Deal custom fields: #{data['data']}"
else
  puts "Error #{response.code}: #{response.body}"
end`
    },
    
    'customers/custom-fields': {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/customers/custom-fields';

fetch(url, {
    method: 'GET',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    }
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        console.log('Customer custom fields:', data.customFields);
    } else {
        console.error('Error:', data.error);
    }
})
.catch(error => console.error('Request failed:', error));`,
      
      python: `import requests

api_key = 'your_api_key_here'
url = 'https://dev.clickai.live/customers/custom-fields'

headers = {
    'Authorization': f'Bearer {api_key}',
    'Content-Type': 'application/json'
}

response = requests.get(url, headers=headers)

if response.status_code == 200:
    data = response.json()
    if data['success']:
        print('Customer custom fields:', data['customFields'])
    else:
        print('Error:', data['error'])
else:
    print(f'HTTP Error {response.status_code}: {response.text}')`,
      
      curl: `curl -X GET "https://dev.clickai.live/customers/custom-fields" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json"`,
      
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/customers/custom-fields';

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 200) {
    $data = json_decode($response, true);
    echo "Customer custom fields: " . json_encode($data['customFields']);
} else {
    echo "Error $http_code: $response";
}
?>`,
      
      ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/customers/custom-fields')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Get.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'

response = http.request(request)

if response.code == '200'
  data = JSON.parse(response.body)
  puts "Customer custom fields: #{data['customFields']}"
else
  puts "Error #{response.code}: #{response.body}"
end`
    },
    
    'products/get-all': {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/products/all';

fetch(url, {
    method: 'GET',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    }
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        console.log('Products:', data.data);
        console.log('Total products:', data.total);
    } else {
        console.error('Error:', data.error);
    }
})
.catch(error => console.error('Request failed:', error));`,
      
      python: `import requests

api_key = 'your_api_key_here'
url = 'https://dev.clickai.live/products/all'

headers = {
    'Authorization': f'Bearer {api_key}',
    'Content-Type': 'application/json'
}

response = requests.get(url, headers=headers)

if response.status_code == 200:
    data = response.json()
    if data['success']:
        print(f'Total products: {data["total"]}')
        for product in data['data']:
            print(f'Product: {product["name"]} - Price: {product["price"]}')
    else:
        print('Error:', data['error'])
else:
    print(f'HTTP Error {response.status_code}: {response.text}')`,
      
      curl: `curl -X GET "https://dev.clickai.live/products/all" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json"`,
      
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/products/all';

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 200) {
    $data = json_decode($response, true);
    echo "Total products: " . $data['total'] . "\\n";
    foreach ($data['data'] as $product) {
        echo "Product: " . $product['name'] . " - Price: $" . $product['price'] . "\\n";
    }
} else {
    echo "Error $http_code: $response";
}
?>`,
      
      ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/products/all')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Get.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'

response = http.request(request)

if response.code == '200'
  data = JSON.parse(response.body)
  puts "Total products: #{data['total']}"
  data['data'].each do |product|
    puts "Product: #{product['name']} - Price: $#{product['price']}"
  end
else
  puts "Error #{response.code}: #{response.body}"
end`
    },
    
    'calendar/create-appointment': {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/calendar/create-appointment';

const appointmentData = {
    title: 'פגישה עם לקוח חשוב',
    description: 'פגישה לדיון בהצעת מחיר',
    start_time: '2024-12-25T09:00:00',
    end_time: '2024-12-25T10:00:00',
    attendees: ['john@example.com', 'jane@company.com'],
    lead_id: 'lead-uuid-here',
    type: 'meeting'
};

fetch(url, {
    method: 'POST',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    },
    body: JSON.stringify(appointmentData)
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        console.log('Appointment created:', data.appointment_id);
    } else {
        console.error('Error:', data.error);
    }
})
.catch(error => console.error('Request failed:', error));`,
      
      python: `import requests
from datetime import datetime, timedelta

api_key = 'your_api_key_here'
url = 'https://dev.clickai.live/calendar/create-appointment'

headers = {
    'Authorization': f'Bearer {api_key}',
    'Content-Type': 'application/json'
}

# Create appointment for tomorrow at 9 AM
start_time = datetime.now() + timedelta(days=1)
start_time = start_time.replace(hour=9, minute=0, second=0, microsecond=0)
end_time = start_time + timedelta(hours=1)

appointment_data = {
    'title': 'פגישה עם לקוח חשוב',
    'description': 'פגישה לדיון בהצעת מחיר',
    'start_time': start_time.isoformat(),
    'end_time': end_time.isoformat(),
    'attendees': ['john@example.com', 'jane@company.com'],
    'lead_id': 'lead-uuid-here',
    'type': 'meeting'
}

response = requests.post(url, headers=headers, json=appointment_data)

if response.status_code == 201:
    data = response.json()
    print(f'Appointment created: {data["appointment_id"]}')
else:
    print(f'Error {response.status_code}: {response.text}')`,
      
      curl: `curl -X POST "https://dev.clickai.live/calendar/create-appointment" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "פגישה עם לקוח חשוב",
    "description": "פגישה לדיון בהצעת מחיר",
    "start_time": "2024-12-25T09:00:00",
    "end_time": "2024-12-25T10:00:00",
    "attendees": ["john@example.com", "jane@company.com"],
    "lead_id": "lead-uuid-here",
    "type": "meeting"
  }'`,
      
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/calendar/create-appointment';

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$appointment_data = [
    'title' => 'פגישה עם לקוח חשוב',
    'description' => 'פגישה לדיון בהצעת מחיר',
    'start_time' => '2024-12-25T09:00:00',
    'end_time' => '2024-12-25T10:00:00',
    'attendees' => ['john@example.com', 'jane@company.com'],
    'lead_id' => 'lead-uuid-here',
    'type' => 'meeting'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($appointment_data));
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 201) {
    $data = json_decode($response, true);
    echo "Appointment created: " . $data['appointment_id'];
} else {
    echo "Error $http_code: $response";
}
?>`,
      
      ruby: `require 'net/http'
require 'json'
require 'time'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/calendar/create-appointment')

# Create appointment for tomorrow at 9 AM
start_time = (Time.now + 24*60*60).strftime('%Y-%m-%dT09:00:00')
end_time = (Time.now + 24*60*60).strftime('%Y-%m-%dT10:00:00')

appointment_data = {
  'title' => 'פגישה עם לקוח חשוב',
  'description' => 'פגישה לדיון בהצעת מחיר',
  'start_time' => start_time,
  'end_time' => end_time,
  'attendees' => ['john@example.com', 'jane@company.com'],
  'lead_id' => 'lead-uuid-here',
  'type' => 'meeting'
}

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'
request.body = appointment_data.to_json

response = http.request(request)

if response.code == '201'
  data = JSON.parse(response.body)
  puts "Appointment created: #{data['appointment_id']}"
else
  puts "Error #{response.code}: #{response.body}"
end`
    }
  };
  
  // נסה למצוא דוגמא ספציפית לendpoint הנוכחי
  const endpointKey = endpoint.path.replace('/', '').replace(/^\//, '');
  
  if (realCodeExamples[endpointKey] && realCodeExamples[endpointKey][language]) {
    return realCodeExamples[endpointKey][language];
  }
  
  // fallback לדוגמאות גנריות אם אין דוגמא ספציפית
  switch (language) {
    case 'javascript':
      return `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live${endpoint.path}';

fetch(url, {
    method: '${endpoint.method}',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    }${endpoint.method !== 'GET' ? `,
    body: JSON.stringify({
        // Your request data here
    })` : ''}
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        console.log('Success:', data);
    } else {
        console.error('Error:', data.error);
    }
})
.catch(error => console.error('Request failed:', error));`;
      
    case 'python':
      return `import requests
import json

api_key = 'your_api_key_here'
url = 'https://dev.clickai.live${endpoint.path}'

headers = {
    'Authorization': f'Bearer {api_key}',
    'Content-Type': 'application/json'
}

${endpoint.method !== 'GET' ? `data = {
    # Your request data here
}

response = requests.${endpoint.method.toLowerCase()}(url, headers=headers, json=data)` : `response = requests.get(url, headers=headers)`}

if response.status_code == 200:
    data = response.json()
    print('Success:', data)
else:
    print(f'Error {response.status_code}: {response.text}')`;
      
    case 'curl':
      return `curl -X ${endpoint.method} "https://dev.clickai.live${endpoint.path}" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json"${endpoint.method !== 'GET' ? ` \\
  -d '{
    "key": "value"
  }'` : ''}`;
      
    case 'php':
      return `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live${endpoint.path}';

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
${endpoint.method !== 'GET' ? `curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode([
    // Your request data here
]));` : ''}

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 200) {
    echo "Success: " . $response;
} else {
    echo "Error $http_code: " . $response;
}
?>`;
      
    case 'ruby':
      return `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live${endpoint.path}')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::${endpoint.method.charAt(0).toUpperCase() + endpoint.method.slice(1).toLowerCase()}.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'
${endpoint.method !== 'GET' ? `request.body = {
  # Your request data here
}.to_json` : ''}

response = http.request(request)

if response.code == '200'
  puts "Success: #{response.body}"
else
  puts "Error #{response.code}: #{response.body}"
end`;
      
    default:
      return ''
  }
}

const getResponseExample = () => {
  const category = currentCategory.value
  const endpoint = getFirstEndpoint()
  
  // דוגמאות Response אמיתיות מהדוקומנטציה
  const realResponseExamples: { [key: string]: string } = {
    'leads/custom-fields': `{
  "success": true,
  "data": [
    {
      "key": "1750878964687_1750878975688",
      "area": "פרטי עסק",
      "type": "text",
      "label": "פרטי עסק - שם העסק",
      "areaId": "1750878964687",
      "required": false
    },
    {
      "key": "1750878964687_1750878993373",
      "area": "פרטי עסק",
      "type": "phone",
      "label": "פרטי עסק - טלפון של בעל העסק",
      "areaId": "1750878964687",
      "required": false
    }
  ]
}`,
    
    'leads/new-lead': `{
  "success": true,
  "message": "Lead created successfully",
  "lead_id": "a1b2c3d4-e5f6-7890-abcd-ef1234567890"
}`,
    
    'deals/custom-fields': `{
  "success": true,
  "data": [
    {
      "key": "deal_priority_1749674482286",
      "area": "פרטי עסקה",
      "type": "select",
      "label": "עדיפות עסקה",
      "areaId": "deal_details",
      "required": false
    },
    {
      "key": "deal_source_1749674482287",
      "area": "מקור",
      "type": "text",
      "label": "מקור העסקה",
      "areaId": "source",
      "required": true
    }
  ]
}`,
    
    'deals/new-deal': `{
  "success": true,
  "message": "Deal created successfully",
  "deal_id": "550e8400-e29b-41d4-a716-446655440000"
}`,
    
    'deals/edit-deal': `{
  "success": true,
  "message": "Deal updated successfully",
  "deal_id": "31539430-8cd0-46bf-be0f-c3a3ab4eb460",
  "updated_fields": ["title", "description", "amount"]
}`,
    
    'customers/custom-fields': `{
  "success": true,
  "customFields": [
    {
      "key": "customer_type_1749674482287",
      "area": "פרטי לקוח",
      "type": "select",
      "label": "סוג לקוח",
      "areaId": "customer_details",
      "required": true,
      "options": ["פרטי", "עסקי", "ממשלתי"]
    },
    {
      "key": "priority_level_1749674482288",
      "area": "פרטי לקוח",
      "type": "text",
      "label": "רמת עדיפות",
      "areaId": "customer_details",
      "required": false
    }
  ]
}`,
    
    'customers/create-customer': `{
  "success": true,
  "customer_id": "550e8400-e29b-41d4-a716-446655440000",
  "message": "Customer created successfully"
}`,
    
    'products/get-all': `{
  "success": true,
  "total": 15,
  "data": [
    {
      "id": "03727424-3596-418b-865c-d7fee7810426",
      "name": "Smart TV 55-inch",
      "price": 2999.99,
      "category": "Electronics",
      "description": "טלוויזיה חכמה 55 אינץ'",
      "sku": "TV-SMART-55",
      "in_stock": true,
      "stock_quantity": 24
    },
    {
      "id": "5d5f3f6b-83a2-4b2c-adfb-b93606480325",
      "name": "Wireless Headphones",
      "price": 299.99,
      "category": "Audio",
      "description": "אוזניות אלחוטיות איכותיות",
      "sku": "HP-WIRELESS-01",
      "in_stock": true,
      "stock_quantity": 56
    }
  ]
}`,
    
    'calendar/create-appointment': `{
  "success": true,
  "appointment_id": "appt_01HGXYZ123456",
  "message": "Appointment created successfully",
  "details": {
    "title": "פגישה עם לקוח חשוב",
    "start_time": "2024-12-25T09:00:00Z",
    "end_time": "2024-12-25T10:00:00Z",
    "attendees": ["john@example.com", "jane@company.com"]
  }
}`,
    
    'team/get-members': `{
  "success": true,
  "total": 8,
  "data": [
    {
      "id": "a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2",
      "name": "John Doe",
      "email": "yossi@company.com",
      "role": "Sales Manager",
      "department": "Sales",
      "active": true
    },
    {
      "id": "b8fb1f9e-65f3-5a0a-c4e4-c9bfb57ff8c3",
      "name": "שרה לוי",
      "email": "sara@company.com",
      "role": "Marketing Specialist",
      "department": "Marketing",
      "active": true
    }
  ]
}`,
    
    'tasks/create-task': `{
  "success": true,
  "task_id": "task_01HGXYZ123456",
  "message": "Task created successfully"
}`,
    
    'contacts/create-contact': `{
  "success": true,
  "contact_id": "contact_01HGXYZ123456",
  "message": "Contact created successfully"
}`,
    
    'chats/send-message': `{
  "success": true,
  "message_id": "msg_01HGXYZ123456",
  "message": "Message sent successfully",
  "status": "delivered"
}`
  };
  
  // נסה למצוא דוגמא ספציפית לendpoint הנוכחי
  const endpointKey = endpoint.path.replace('/', '').replace(/^\//, '');
  
  if (realResponseExamples[endpointKey]) {
    return realResponseExamples[endpointKey];
  }
  
  // fallback לדוגמא גנרית לפי קטגוריה
  const fallbackExamples = {
    leads: realResponseExamples['leads/custom-fields'],
    deals: realResponseExamples['deals/custom-fields'],
    customers: realResponseExamples['customers/custom-fields'],
    products: realResponseExamples['products/get-all'],
    calendar: realResponseExamples['calendar/create-appointment'],
    team: realResponseExamples['team/get-members'],
    tasks: realResponseExamples['tasks/create-task'],
    contacts: realResponseExamples['contacts/create-contact'],
    chats: realResponseExamples['chats/send-message']
  };
  
  return fallbackExamples[category] || realResponseExamples['leads/custom-fields'];
}

const copyCodeToClipboard = async () => {
  try {
    const codeText = getCodeExample(selectedLanguage.value)
    
    // Use the Clipboard API if available
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(codeText)
    } else {
      // Fallback for older browsers or non-secure contexts
      const textArea = document.createElement('textarea')
      textArea.value = codeText
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      document.execCommand('copy')
      textArea.remove()
    }
    
    // Show success feedback
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, 2000) // Reset after 2 seconds
    
  } catch (err) {
    console.error('Failed to copy code: ', err)
    // Could add error notification here
  }
}

onMounted(() => {
  // Get category from route params
  currentCategory.value = route.params.category as string || 'leads'
  
  // פתיחת הקטגוריה הנכונה בהתבסס על הקטגוריה הנוכחית
  let categoryName = ''
  
  if (currentCategory.value === 'leads') {
    categoryName = 'Leads'
  } else if (currentCategory.value === 'deals') {
    categoryName = 'Deals'
  } else if (currentCategory.value === 'customers') {
    categoryName = 'Customers'
  } else if (currentCategory.value === 'products') {
    categoryName = 'Products'
  } else if (currentCategory.value === 'calendar') {
    categoryName = 'Calendar'
  } else if (currentCategory.value === 'team') {
    categoryName = 'Team'
  } else if (currentCategory.value === 'tasks') {
    categoryName = 'Tasks'
  } else if (currentCategory.value === 'contacts') {
    categoryName = 'Contacts'
  } else if (currentCategory.value === 'chats') {
    categoryName = 'Chats'
  }
  
  if (categoryName) {
    openSections.value = [categoryName]
  }
})

// Watch for route changes to update category
watch(() => route.params.category, (newCategory) => {
  if (newCategory) {
    currentCategory.value = newCategory as string
    
    // פתיחת הקטגוריה הנכונה בהתבסס על הקטגוריה החדשה
    let categoryName = ''
    
    if (newCategory === 'leads') {
      categoryName = 'Leads'
    } else if (newCategory === 'deals') {
      categoryName = 'Deals'
    } else if (newCategory === 'customers') {
      categoryName = 'Customers'
    } else if (newCategory === 'products') {
      categoryName = 'Products'
    } else if (newCategory === 'calendar') {
      categoryName = 'Calendar'
    } else if (newCategory === 'team') {
      categoryName = 'Team'
    } else if (newCategory === 'tasks') {
      categoryName = 'Tasks'
    } else if (newCategory === 'contacts') {
      categoryName = 'Contacts'
    } else if (newCategory === 'chats') {
      categoryName = 'Chats'
    }
    
    if (categoryName) {
      openSections.value = [categoryName]
    }
  }
})
</script>

<style scoped>
/* Hide scrollbars */
::-webkit-scrollbar {
  display: none;
}

* {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.font-mono {
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.transition {
  transition: all 0.15s ease-in-out;
}

pre {
  white-space: pre-wrap;
  word-wrap: break-word;
}

/* Method badge colors */
.bg-green-100 { background-color: #dcfce7; }
.text-green-800 { color: #166534; }
.bg-blue-100 { background-color: #dbeafe; }
.text-blue-800 { color: #1e40af; }
.bg-yellow-100 { background-color: #fef3c7; }
.text-yellow-800 { color: #92400e; }
.bg-red-100 { background-color: #fee2e2; }
.text-red-800 { color: #991b1b; }

/* Custom colors */
.bg-custom-blue { background-color: #F0F9FF; }
.text-custom-dark-blue { color: #075985; }
.text-custom-medium-blue { color: #0369A1; }
.bg-custom-button-blue { background-color: #0284C7; }
.text-custom-button-blue { color: #0284C7; }
.hover\:text-custom-dark-blue:hover { color: #075985; }
.hover\:bg-custom-button-blue:hover { background-color: #0284C7; }
.focus\:ring-custom-button-blue:focus { --tw-ring-color: #0284C7; }
</style> 